/**
 * Player-view paperdoll — the survivor's own record (press I to toggle).
 *
 * Layout is a fixed left column and a tabbed right column:
 *  - **Left**: the animated 3D preview (`CharacterPreview`). Always visible,
 *    because both tabs are about the survivor it shows.
 *  - **Right, `Inventory`**: TWO framed blocks — **On you** (the six equipment
 *    slots, the carry meters, and the carried bag) and **Hideout** (the stash
 *    and its slot meter).
 *  - **Right, `Skills`**: raw attributes with allocation, the blended derived
 *    stats, and specialization standing.
 *
 * ─── The two blocks, and where the equipment slots went (2026-09-16) ───────
 * Janeks: *"change the inventory, have the carry on and hide out both in their
 * own blocks, so 2 blocks separated - Player equipped + inventory and a hideout
 * section"*. The split the panel now draws is the one the RULES already have:
 * inventory.md's two pools differ in what dying costs you, and what you are
 * wearing dies with you the same way the bag does. So equipment + carried is
 * one block and the hideout is the other.
 *
 * The cost is real and worth stating: the equipment slots used to live under
 * the preview and were therefore visible on the Skills tab too. They are now
 * inside the Inventory pane. What Skills keeps is the *effect* of that gear —
 * the "With gear" derived stats — which is what that tab is for.
 *
 * ─── Slots, not rows ───────────────────────────────────────────────────────
 * The pools render through `ui/item-slot.ts`: a square well with the art at
 * rest and its actions revealed on hover, plus `ui/item-tooltip.ts`'s floating
 * card. The previous shape was a list row — a 26 px icon, the name, the weight
 * and a permanently visible button strip — which is a table with pictures,
 * not a bag. Both modules are shared with the trade panel so an item looks and
 * behaves the same wherever it is handled.
 *
 * **On "Skills".** `character-stats.md` v2 explicitly supersedes the earlier
 * "5 vocational skills" model, so there is no skill tree to render. What
 * exists, and what this tab is, is the two progression surfaces the backend
 * actually ships: banked level-up points spent on the five attributes
 * (`POST /character/allocate`), and the L5 leader specialization. The
 * allocation half had never been reachable from the UI at all.
 *
 * Derived stats come from `equipment.blendedStats`, not `character.stats` —
 * the latter stays base-only even with gear equipped (see game-state.ts's
 * docstring).
 *
 * Scope note: these are single-item buttons hitting the same batch socket
 * endpoints inventory.md designed for staged, multi-item triage (rule 6's
 * all-or-nothing submit) — a legitimate use of those endpoints, but NOT that
 * batched staging/triage flow. There's no "stage several moves, review,
 * submit once" UI here. `packForRaid` (rule 7) is greedy and never rejects on
 * capacity — an item that doesn't fit the carried dual cap just silently
 * stays in HIDEOUT with no server-side error, so Pack pre-checks capacity
 * client-side and shows a message rather than emitting a call that would
 * silently do nothing.
 */
import {
  gameStateStore, equipItem, unequipItem, storeItem, packItem, discardItem, allocateAttributes, useItem,
} from '../../state/game-state';
import {
  EQUIPMENT_SLOTS, type EquipmentSlot, type EquippedItemSummary, type InventoryItemSummary,
  type AttributeCode, type UseItemResult,
} from '../../state/game-state.types';
import { capacityLevel, fillPercent, worstLevel } from './capacity';
import { disposeWardrobePreview, wardrobePreview } from './wardrobe-preview';
import { categoryIconPath, type CategoryProbe } from './category-icon';
import {
  consumableEffectText, inventoryTooltipHtml, ItemTooltip, itemTooltipHtml,
} from './item-tooltip';
import { closeOtherModals } from './panels';
import {
  emptySlotHtml, gridSlotCount, slotGridStyle, slotHtml,
  type SlotAction,
} from './item-slot';
import { createCloseButton } from './close-button';
import { escapeHtml } from '../../util';

/**
 * Icon assets, resolved dynamically rather than one static import per file.
 * A hand-maintained list of ~40 (`import xIcon from '...'` + an `ICON_MAP`
 * entry each) was already the whole reason this comment used to live here;
 * the 2026-09-12 generated batch alone adds ~130 more keys, several with 3
 * size tiers apiece — hand-listing that is a liability, not a style choice.
 * `import.meta.glob(..., { eager: true })` pulls in every file under
 * assets/icons/ at build time (still fingerprinted/bundled by Vite, same as
 * a static import — a bare runtime path would still 404 in production),
 * reduced here to a lookup by bare filename matching the `iconPath` values
 * seeded in backend/prisma/seed.ts.
 */
const ICON_MODULES = import.meta.glob<string>('../assets/icons/*.{png,webp}', {
  eager: true, query: '?url', import: 'default',
});
const ICON_MAP: Record<string, string> = {};
for (const [path, url] of Object.entries(ICON_MODULES)) {
  ICON_MAP[path.slice(path.lastIndexOf('/') + 1)] = url;
}

/**
 * Three size tiers for icons built by
 * `assets/generated_items/build_multisize_icons.py` (`<key>_icon.webp` =
 * detail/default, `<key>_icon_slot.webp` = 64px, `<key>_icon_full.webp` =
 * 512px). Every OLDER icon only has the one plain `<key>_icon.{png,webp}`
 * file, so slot/full both fall back to it — an icon that predates the
 * multi-size pipeline should keep rendering, not go blank.
 */
function iconTiers(iconPath: string): { slot: string; detail: string; full: string } {
  const detail = ICON_MAP[iconPath] ?? '';
  const slot = ICON_MAP[iconPath.replace(/_icon\.(png|webp)$/, '_icon_slot.webp')] ?? detail;
  const full = ICON_MAP[iconPath.replace(/_icon\.(png|webp)$/, '_icon_full.webp')] ?? detail;
  return { slot, detail, full };
}

const SLOT_LABELS: Record<EquipmentSlot, string> = {
  HEADGEAR: 'Headgear',
  TORSO: 'Torso',
  VEST: 'Vest',
  LEGS: 'Legs',
  GLOVES: 'Gloves',
  FEET: 'Footwear',
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
  BACKPACK: 'Backpack',
};

/**
 * The one place the sheet's field names and the allocate endpoint's wire codes
 * are paired (`AttributeCode` documents why they differ). `note` is what the
 * point actually buys, taken from character-stats.md's own formulas rather
 * than invented — a player spending an irreversible point deserves to know.
 */
const ATTRIBUTE_ROWS: Array<{
  key: 'strength' | 'intellect' | 'stamina' | 'agility' | 'perception';
  code: AttributeCode;
  label: string;
  note: string;
}> = [
  { key: 'strength', code: 'STR', label: 'Strength', note: 'Carry weight, melee power, heavy gear requirements' },
  { key: 'intellect', code: 'INT', label: 'Intellect', note: 'Craft quality and speed, minor energy regen' },
  { key: 'stamina', code: 'STA', label: 'Stamina', note: 'Max HP and resilience' },
  { key: 'agility', code: 'AGI', label: 'Agility', note: 'Move speed, crouch speed, energy regen (major)' },
  { key: 'perception', code: 'PER', label: 'Perception', note: 'Loot discovery, ranged accuracy, stealth' },
];

/**
 * Display names for `Specialization` (schema.prisma). The sheet carries a raw
 * `specializationId`, so an id added server-side without a label here falls
 * back to the raw value rather than rendering blank.
 */
const SPECIALIZATION_LABELS: Record<string, string> = {
  SCAVENGING: 'Scavenging',
  BOUNTY_HUNTING: 'Bounty Hunting',
  ENGINEERING: 'Engineering',
  COOKING: 'Cooking',
  TAILORING: 'Tailoring',
};

const TABS = [
  { id: 'inventory', label: 'Inventory' },
  { id: 'skills', label: 'Skills' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const STAT_ROWS: Array<{ key: 'power' | 'carryWeight' | 'hpAmount' | 'walkSpeed' | 'runSpeed'; label: string; digits: number }> = [
  { key: 'power', label: 'Power', digits: 0 },
  { key: 'carryWeight', label: 'Carry (kg)', digits: 1 },
  { key: 'hpAmount', label: 'Max HP', digits: 0 },
  { key: 'walkSpeed', label: 'Walk (m/s)', digits: 2 },
  { key: 'runSpeed', label: 'Run (m/s)', digits: 2 },
];

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Action failed';
}

/**
 * `<picture>` with a responsive `srcset` so a 22-26px equipment/inventory
 * slot (`.bv-pd-slot-icon`/`.bv-pd-item-icon`, styles.ts) loads the 64px
 * tier over the network, not the flat 512px this project used to ship to
 * every render size alike. `sizesHint` is the CSS box `cssClass` actually
 * renders at — passed in rather than hardcoded so a future bigger consumer
 * (the queued Salvage Codex `.inspect-card`) reuses this helper instead of
 * growing a second one.
 */
export function pictureHtml(
  iconPath: string | null,
  altText: string,
  cssClass: string,
  sizesHint: string,
  /** What the item IS, so a missing icon falls back to its category art instead of a blank. */
  fallback?: CategoryProbe,
): string {
  let tiers = iconPath ? iconTiers(iconPath) : null;
  if ((!tiers || !tiers.detail) && fallback) tiers = iconTiers(categoryIconPath(fallback));
  if (!tiers || !tiers.detail) return '';
  const { slot, detail, full } = tiers;
  // No `type` attribute — this is resolution switching (3 sizes of the same
  // file), not format switching, and the legacy single-tier icons are still
  // a mix of .png/.webp, so a hardcoded `type="image/webp"` would be wrong
  // for those.
  return `<picture>
    <source srcset="${slot} 64w, ${detail} 256w, ${full} 512w" sizes="${sizesHint}">
    <img class="${cssClass}" src="${detail}" alt="${escapeHtml(altText)}" />
  </picture>`;
}

/**
 * Carried/hideout pools render at `SLOT_COLUMNS` across with `SLOT_MIN_ROWS`
 * rows always drawn — 15 slots — growing by whole rows past that. The rule and
 * the markup live in `ui/item-slot.ts`; both pools and both trade columns use
 * it, so a bag looks the same wherever it appears.
 *
 * Unlike the old row list, a slot DOES show a stack count: `quantity` has been
 * on the wire since ammo shipped, this file's own mirror simply never declared
 * it, and 50 rounds rendering as one bullet is the visible half of that.
 */

/**
 * Where an item currently is — decides what "Transfer" means for it.
 *
 * One button, two destinations: carried moves to the hideout, hideout packs
 * into the bag. The request asked for one Transfer control per slot, and the
 * two underlying calls are genuinely different (`inventory:moveToHideout`
 * vs. `packForRaid`, with a client-side capacity pre-check on the second),
 * so the pool has to travel with the click.
 */
type Pool = 'carried' | 'hideout';

/**
 * 6TH SKILL — COMING IN A FUTURE UPDATE.
 *
 * A sixth attribute is planned and not built. The card is a placeholder, not a
 * row of `ATTRIBUTE_ROWS`: it has no code, no value, no `+` button, and nothing
 * on the server knows about it. It renders as a rusted, taped-off plate so the
 * grid reads as complete and the player knows more is coming. It carries NO
 * text on the card (Janeks, 2026-10-13); the hover title and aria-label say what
 * it is. When the
 * attribute ships, delete this constant and add its row to `ATTRIBUTE_ROWS`.
 *
 * The tape X is two bands: one fully stuck, one half peeled off with the loose
 * end hanging down and fluttering (see paperdoll.css.ts, bvpd-tape-*).
 */
const SIXTH_SKILL_CARD_HTML = `<div class="bv-pd-attr-card bv-pd-attr-locked" aria-disabled="true"
    aria-label="Sixth skill, coming in a future update" title="Coming in a future update">
    <span class="bv-pd-tape bv-pd-tape-stuck-a" aria-hidden="true"></span>
    <span class="bv-pd-tape bv-pd-tape-stuck-b" aria-hidden="true"></span>
    <span class="bv-pd-tape-fall" aria-hidden="true"><span class="bv-pd-tape bv-pd-tape-loose"></span></span>
  </div>`;

/** The Record's bag and stash are 6 across, 5 rows showing before they grow. */
const PD_COLUMNS = 6;
const PD_MIN_ROWS = 5;

export class Paperdoll {
  readonly el: HTMLDivElement;
  private isOpen = false;
  private readonly unsub: () => void;
  private readonly slotEls = new Map<EquipmentSlot, HTMLElement>();
  // Shared with the Atelier's fitting stage — see wardrobe-preview.ts.
  private readonly preview = wardrobePreview();
  private previewBox: HTMLElement | null = null;
  private readonly tabEls = new Map<TabId, HTMLButtonElement>();
  private readonly paneEls = new Map<TabId, HTMLElement>();
  private titleEl!: HTMLElement;
  private attrsEl!: HTMLElement;
  private statsEl!: HTMLElement;
  private specEl!: HTMLElement;
  private capacityEl!: HTMLElement;
  private carriedEl!: HTMLElement;
  private hideoutEl!: HTMLElement;
  private carriedSubEl!: HTMLElement;
  private hideoutSubEl!: HTMLElement;
  private msgEl!: HTMLElement;
  private lastActionError: string | null = null;
  private tab: TabId = 'inventory';

  /** Points moved but not yet spent — see the staging block below `runAction`. */
  private staged: Partial<Record<AttributeCode, number>> = {};

  private savingPoints = false;
  private tooltip!: ItemTooltip;
  /** The card a CLICK on a slot pins: it carries the action buttons and stays until a click lands outside it. */
  private pinned!: ItemTooltip;
  private drag: {
    itemId: string; slot: EquipmentSlot | null; from: Pool; x: number; y: number; started: boolean;
    pointerType: string; ghost: HTMLElement | null; timer: number | null; srcEl: HTMLElement;
  } | null = null;
  private suppressClick = false;
  /**
   * Which pool each rendered item is in, by `UserItem.id`.
   *
   * Rebuilt on every render from the sheet rather than written into the DOM as
   * a `data-pool`: the pool is what decides what Transfer DOES, and reading it
   * back out of an attribute would let a stale cell (one the store replaced
   * mid-click) dispatch the wrong call.
   */
  private readonly poolById = new Map<string, Pool>();
  private readonly itemById = new Map<string, InventoryItemSummary>();

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'bv-paperdoll';
    this.el.hidden = true;
    this.build();
    parent.appendChild(this.el);
    this.unsub = gameStateStore.subscribe(() => { if (this.isOpen) this.render(); });
  }

  get open(): boolean { return this.isOpen; }

  show(): void {
    // The Record is a WINDOW even though it does not block movement — see the
    // `isModal` note in `ui/panels.ts`. It is registered by `index.ts` (it is
    // constructed there, not self-mounted like the other panels), so the id is
    // spelled out here rather than imported from a constant that would have to
    // live somewhere neither file owns.
    closeOtherModals('record');
    this.isOpen = true;
    this.el.hidden = false;
    // Only now does the preview build its WebGL context and load the GLB —
    // see CharacterPreview's docstring on not rendering behind a shut panel.
    // Back from the Atelier's stage, if it borrowed the preview, and with
    // nothing it was trying on.
    if (this.previewBox) this.preview.mount(this.previewBox);
    this.preview.setFit(null);
    this.preview.setActive(true);
    this.render();
  }

  hide(): void {
    this.isOpen = false;
    this.el.hidden = true;
    this.preview.setActive(false);
    this.pinned.hide();
    // Unsaved is unsaved. Carrying a staged allocation across a close would
    // let the player come back to points the server never heard of, sitting
    // beside a bank that may have changed in the meantime.
    this.staged = {};
  }

  toggle(): void {
    if (this.isOpen) this.hide();
    else this.show();
  }

  private build(): void {
    const panel = document.createElement('div');
    panel.className = 'bv-pd-panel';
    this.el.appendChild(panel);

    const header = document.createElement('div');
    header.className = 'bv-pd-header';
    this.titleEl = document.createElement('span');
    this.titleEl.textContent = 'Survivor Record';
    header.appendChild(this.titleEl);
    const closeBtn = createCloseButton({ label: 'Close', className: 'bv-pd-close bv-close-inline', onClick: () => this.hide() });
    header.appendChild(closeBtn);
    panel.appendChild(header);

    this.msgEl = document.createElement('div');
    this.msgEl.className = 'bv-pd-msg';
    this.msgEl.hidden = true;
    panel.appendChild(this.msgEl);

    const body = document.createElement('div');
    body.className = 'bv-pd-body';
    panel.appendChild(body);

    // ── Left column: the survivor themselves ──────────────────────────────
    const left = document.createElement('div');
    left.className = 'bv-pd-left';
    body.appendChild(left);

    const previewBox = document.createElement('div');
    previewBox.className = 'bv-pd-preview';
    // Closer than the shared default: the survivor fills more of this box.
    previewBox.dataset.cpDistance = '4.6';
    previewBox.innerHTML = '<span class="bv-pd-preview-hint">drag to turn</span>';
    left.appendChild(previewBox);
    this.previewBox = previewBox;
    this.preview.mount(previewBox);

    // The worn slots live UNDER the preview, beside the body they dress
    // (Janeks, 2026-10-13), not in the bag's block: the bag and the stash get
    // the right-hand side to themselves and can be 6 x 5.
    const worn = document.createElement('div');
    worn.className = 'bv-block bv-pd-worn';
    worn.dataset.block = 'worn';
    worn.innerHTML = '<div class="bv-block-head"><span class="bv-block-title">Worn</span></div>';
    left.appendChild(worn);

    // The seven worn slots, still `.bv-pd-slot` cells (a labelled row, not an
    // art square): a slot the game names — "Headgear", "Primary" — is a
    // different object from a bag cell, and losing the label to a thumbnail
    // would make an empty paperdoll unreadable.
    const slotsGrid = document.createElement('div');
    slotsGrid.className = 'bv-pd-slots';
    for (const slot of EQUIPMENT_SLOTS) {
      const cell = document.createElement('div');
      cell.className = 'bv-pd-slot';
      cell.dataset.tip = `eq:${slot}`;
      cell.innerHTML = `<div class="bv-pd-slot-label">${SLOT_LABELS[slot]}</div><div class="bv-pd-slot-item">Empty</div>`;
      slotsGrid.appendChild(cell);
      this.slotEls.set(slot, cell);
    }
    worn.appendChild(slotsGrid);

    // ── Right column: tabs ────────────────────────────────────────────────
    const right = document.createElement('div');
    right.className = 'bv-pd-right';
    body.appendChild(right);

    const tabBar = document.createElement('div');
    tabBar.className = 'bv-pd-tabs';
    tabBar.setAttribute('role', 'tablist');
    for (const t of TABS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bv-pd-tab';
      btn.textContent = t.label;
      btn.setAttribute('role', 'tab');
      btn.dataset.tab = t.id;
      tabBar.appendChild(btn);
      this.tabEls.set(t.id, btn);
    }
    right.appendChild(tabBar);

    const invPane = document.createElement('div');
    invPane.className = 'bv-pd-pane';
    this.paneEls.set('inventory', invPane);
    right.appendChild(invPane);

    const skillsPane = document.createElement('div');
    skillsPane.className = 'bv-pd-pane';
    this.paneEls.set('skills', skillsPane);
    right.appendChild(skillsPane);

    // Skills pane content: attributes (allocatable), derived stats, spec.
    this.attrsEl = document.createElement('div');
    this.attrsEl.className = 'bv-pd-attrs';
    this.statsEl = document.createElement('div');
    this.statsEl.className = 'bv-pd-stats';
    this.specEl = document.createElement('div');
    this.specEl.className = 'bv-pd-spec';
    // Derived stats take a third of the row and the specialization the other
    // two thirds (Janeks, 2026-10-13), so they share one grid row rather than
    // stacking full-width.
    const lowerRow = document.createElement('div');
    lowerRow.className = 'bv-pd-lower';
    lowerRow.append(this.statsEl, this.specEl);
    skillsPane.append(this.attrsEl, lowerRow);

    // ── Inventory pane: two blocks ────────────────────────────────────────
    // ON YOU holds what dies with the survivor — the six worn slots and the
    // bag. HIDEOUT holds what does not. The capacity meters split the same
    // way: weight and carried slots are co-equal caps (inventory.md rule 3,
    // "whichever hits first") so both live in the first block, while the
    // hideout is slot-only by design (rule 4 — no weight limit in the
    // settlement) and carries its one meter in the second.
    const blocks = document.createElement('div');
    blocks.className = 'bv-inv-blocks';
    invPane.appendChild(blocks);

    const onYou = document.createElement('div');
    onYou.className = 'bv-block';
    onYou.dataset.block = 'onyou';
    onYou.innerHTML = '<div class="bv-block-head">'
      + '<span class="bv-block-title"><img class="bv-block-icon" src="/icons/ui/backpack.webp" alt="" width="28" height="28" decoding="async" draggable="false">On you</span>'
      + '<span class="bv-block-sub" data-carried-sub></span></div>';
    blocks.appendChild(onYou);

    // ONE meter, the HUD's exact shape: slots on the head line, weight in the
    // bar. Both caps are shown because either can block the bag (rule 3), and
    // the bar's COLOUR is the worse of the two — a bar tracking weight alone
    // reads healthy on a slot-blocked bag.
    this.capacityEl = document.createElement('div');
    this.capacityEl.innerHTML = `
      <div class="bv-load" data-cap-weight>
        <div class="bv-load-head"><span class="bv-load-label">Load</span><span class="bv-load-slots" data-slots-text>—</span></div>
        <div class="bv-load-bar">
          <div class="bv-load-fill" data-weight-fill></div>
          <span class="bv-load-text" data-weight-text>—</span>
        </div>
      </div>`;
    onYou.appendChild(this.capacityEl);

    this.carriedEl = document.createElement('div');
    this.carriedEl.className = 'bv-slots bv-pd-items';
    this.carriedEl.dataset.pool = 'carried';
    this.carriedEl.style.cssText = slotGridStyle(PD_COLUMNS);
    onYou.appendChild(this.carriedEl);

    const hideoutBlock = document.createElement('div');
    hideoutBlock.className = 'bv-block';
    hideoutBlock.dataset.block = 'hideout';
    hideoutBlock.innerHTML = '<div class="bv-block-head">'
      + '<span class="bv-block-title"><img class="bv-block-icon" src="/icons/ui/chest.webp" alt="" width="28" height="28" decoding="async" draggable="false">Hideout</span>'
      + '<span class="bv-block-sub" data-hideout-sub></span></div>'
      + '<div class="bv-load" data-cap-hideout>'
      + '<div class="bv-load-head"><span class="bv-load-label">Stash</span></div>'
      + '<div class="bv-load-bar"><div class="bv-load-fill" data-hideout-fill></div>'
      + '<span class="bv-load-text" data-hideout-text>—</span></div></div>';
    blocks.appendChild(hideoutBlock);

    this.hideoutEl = document.createElement('div');
    this.hideoutEl.className = 'bv-slots bv-pd-items';
    this.hideoutEl.dataset.pool = 'hideout';
    this.hideoutEl.style.cssText = slotGridStyle(PD_COLUMNS);
    hideoutBlock.appendChild(this.hideoutEl);

    this.carriedSubEl = onYou.querySelector('[data-carried-sub]') as HTMLElement;
    this.hideoutSubEl = hideoutBlock.querySelector('[data-hideout-sub]') as HTMLElement;

    // event delegation — one listener for every action button, slot buttons
    // included; row content is rebuilt wholesale on every render()
    panel.addEventListener('click', this.onClick);

    // ── Hover tooltip ─────────────────────────────────────────────────────
    // One card for the whole panel, on document.body — NOT on the grid (which
    // scrolls, and would clip the bottom row's card) and NOT on `this.el`,
    // which is `z-index: 15` and would therefore cap the card BELOW the panel
    // scrims and the player frame. See ItemTooltip's docstring.
    // Delegated, so 30 slots cost one listener and a card is built only for
    // the slot actually under the pointer.
    this.tooltip = new ItemTooltip();
    this.pinned = new ItemTooltip();
    this.pinned.el.classList.add('bv-tip-pinned');
    this.pinned.el.setAttribute('role', 'dialog');
    this.pinned.el.addEventListener('click', this.onPinnedClick);
    document.addEventListener('pointerdown', this.onDocPointerDown, true);
    panel.addEventListener('pointerdown', this.onSlotPointerDown);
    // The browser's own image drag would otherwise take over a mouse drag on the
    // item art and cancel the pointer stream this drag is built on (it fires
    // pointercancel), which is invisible in jsdom and fatal in a real browser.
    panel.addEventListener('dragstart', (e) => e.preventDefault());
    panel.addEventListener('dblclick', this.onDoubleClick);
    panel.addEventListener('pointerover', this.onPointerOver);
    panel.addEventListener('pointermove', this.onPointerMove);
    panel.addEventListener('pointerout', this.onPointerOut);
    // A scroll moves the slot out from under a card that is still following
    // the old coordinates, and the panel itself scrolls too.
    panel.addEventListener('scroll', this.onScrollAway, true);
  }

  // ─── tooltip wiring ───────────────────────────────────────────────────────

  private tipHtmlFor(tipId: string): string | null {
    if (tipId.startsWith('eq:')) {
      const slot = tipId.slice(3) as EquipmentSlot;
      const worn = gameStateStore.get().equipment?.slots[slot];
      if (!worn) return null;
      // An equipped piece has an INSTANCE state the catalog does not: its
      // durability, and whether it has broken. That is the whole reason this
      // branch exists rather than reusing the inventory one.
      const def = worn.def;
      return itemTooltipHtml({
        name: def.name,
        kind: def.kind,
        stats: {
          rarity: def.rarity,
          value: 0,
          weight: 0,
          slot: def.slot,
          armorClass: def.armorClass,
          armorPoints: def.armorPoints,
          baseDamage: def.baseDamage,
          fixedDamage: def.fixedDamage,
          ammoType: def.ammoType,
          maxDurability: def.maxDurability,
          statMods: {},
          attrReq: null,
        },
        priceLine: `Condition ${worn.durability}/${worn.maxDurability}${worn.broken ? ' · BROKEN' : ''}`,
      });
    }
    const item = this.itemById.get(tipId);
    if (!item) return null;
    return inventoryTooltipHtml(item);
  }

  private readonly onPointerOver = (e: PointerEvent): void => {
    if (this.pinned.isVisible || this.drag?.started) return;
    const host = (e.target as HTMLElement | null)?.closest('[data-tip]') as HTMLElement | null;
    if (!host?.dataset.tip) return;
    const html = this.tipHtmlFor(host.dataset.tip);
    if (!html) { this.tooltip.hide(); return; }
    this.tooltip.show(html, e.clientX, e.clientY);
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.tooltip.isVisible) return;
    if (!(e.target as HTMLElement | null)?.closest('[data-tip]')) { this.tooltip.hide(); return; }
    this.tooltip.move(e.clientX, e.clientY);
  };

  private readonly onPointerOut = (e: PointerEvent): void => {
    const from = (e.target as HTMLElement | null)?.closest('[data-tip]');
    if (!from) return;
    // `pointerout` also fires crossing between a slot's own children; only a
    // move that actually leaves the slot should dismiss the card.
    const to = (e.relatedTarget as HTMLElement | null)?.closest('[data-tip]');
    if (to === from) return;
    this.tooltip.hide();
  };

  private readonly onScrollAway = (): void => this.tooltip.hide();

  private onClick = (e: MouseEvent): void => {
    const tabBtn = (e.target as HTMLElement).closest('button[data-tab]') as HTMLButtonElement | null;
    if (tabBtn?.dataset.tab) {
      this.tab = tabBtn.dataset.tab as TabId;
      this.render();
      return;
    }

    if (this.suppressClick) return;

    const btn = (e.target as HTMLElement).closest('button[data-action]') as HTMLButtonElement | null;
    if (!btn) {
      const host = (e.target as HTMLElement).closest('[data-tip]') as HTMLElement | null;
      if (host?.dataset.tip) this.pin(host);
      return;
    }
    // A worn slot's × is a button inside a pinnable cell; it acts, not pins.
    this.pinned.hide();
    const { action, itemId, slot, attr } = btn.dataset;

    if (action === 'allocate' && attr) {
      this.stage(attr as AttributeCode, 1);
    } else if (action === 'unstage' && attr) {
      this.stage(attr as AttributeCode, -1);
    } else if (action === 'stage-save') {
      this.saveStaged();
    } else if (action === 'stage-reset') {
      this.staged = {};
      this.clearMessage();
      this.render();
    } else if (action === 'use' && itemId) {
      this.clearMessage();
      useItem(itemId).then(
        (result) => this.showMessage(this.useReceipt(result)),
        (err) => this.showMessage(errorMessage(err)),
      );
    } else if (action === 'equip' && itemId) {
      // The target slot comes from the ITEM, not from a `data-slot` on the
      // button: the slot grid renders one generic button shape for every
      // action, and threading a per-action attribute through it to carry one
      // action's argument is how a button and its payload drift apart.
      const target = slot ?? this.itemById.get(itemId)?.slot;
      if (target) this.runAction(equipItem(itemId, target as EquipmentSlot));
    } else if (action === 'unequip' && slot) {
      this.runAction(unequipItem(slot as EquipmentSlot));
    } else if (action === 'transfer' && itemId) {
      // ONE button, two calls. Which one is decided by where the item IS, read
      // from the render's own pool map rather than from a `data-` attribute,
      // so a cell the store replaced mid-click cannot dispatch the wrong move.
      if (this.poolById.get(itemId) === 'hideout') this.tryPack(itemId);
      else { this.clearMessage(); storeItem(itemId); }
    } else if (action === 'store' && itemId) {
      this.clearMessage();
      storeItem(itemId);
    } else if (action === 'pack' && itemId) {
      this.tryPack(itemId);
    } else if (action === 'discard' && itemId) {
      this.clearMessage();
      discardItem(itemId);
    }
  };

  private runAction(promise: Promise<void>): void {
    this.clearMessage();
    promise.catch((err) => this.showMessage(errorMessage(err)));
  }

  // ─── staged attribute points ──────────────────────────────────────────────
  //
  // Janeks: *"Skill points adding -> add skill points and then hit 'save' to
  // apply."* Until now every `+` spent a point immediately and irreversibly.
  //
  // The buffer is UI-only and deliberately not in `gameStateStore`: it is not
  // game state, nothing else reads it, and a store write would repaint every
  // subscriber on each `+`. It is dropped on close (`hide()`) for the same
  // reason a half-typed form is — unsaved is unsaved, and carrying it back
  // would let the player return to a staged set the server never heard of.

  /** Moves one point on or off `code`, within both ceilings. */
  private stage(code: AttributeCode, delta: number): void {
    const character = gameStateStore.get().character;
    if (character?.state !== 'ACTIVE') return;
    const row = ATTRIBUTE_ROWS.find((r) => r.code === code);
    if (!row) return;

    const current = this.staged[code] ?? 0;
    const next = current + delta;
    if (next < 0) return;
    if (delta > 0) {
      if (this.stagedTotal() >= character.attributePoints) return;
      // The ceiling is the SERVER's `attrMax`, and it is checked here so the
      // `+` goes dead AT the cap. Without it the whole staged set is rejected
      // on Save with ATTRIBUTE_MAXED and the player loses every point they
      // staged to find out.
      if (character.attributes[row.key] + next > character.attrMax) return;
    }
    this.staged[code] = next;
    this.clearMessage();
    this.render();
  }

  private stagedTotal(): number {
    return Object.values(this.staged).reduce((n, v) => n + v, 0);
  }

  /**
   * Save / Reset, and nothing at all until something is staged.
   *
   * A permanently visible Save on an empty buffer is a button that does
   * nothing, and this panel already has one row of those per item.
   */
  private stagedFooter(stagedTotal: number): string {
    if (stagedTotal === 0) return '';
    return `<div class="bv-pd-stage-foot">
      <span class="bv-pd-stage-count">${stagedTotal} staged</span>
      <button type="button" class="bv-act bv-act-ghost bv-pd-stage-btn" data-action="stage-reset"
        ${this.savingPoints ? 'disabled' : ''}>Reset</button>
      <button type="button" class="bv-act bv-act-go bv-pd-stage-btn" data-action="stage-save"
        ${this.savingPoints ? 'disabled' : ''}>${this.savingPoints ? 'Saving…' : 'Save'}</button>
    </div>`;
  }

  /**
   * ONE request for the whole set, never a loop over the single-point route:
   * that loop can hit the per-attribute ceiling on call 3 of 5 and leave the
   * first two spent with nothing to roll them back. `allocateAttributes` posts
   * the batch route, which spends all of it or none of it.
   */
  private saveStaged(): void {
    if (this.stagedTotal() === 0 || this.savingPoints) return;
    const staged = { ...this.staged };
    this.savingPoints = true;
    this.clearMessage();
    this.render();
    allocateAttributes(staged).then(
      () => {
        this.staged = {};
        this.savingPoints = false;
        this.render();
      },
      (err) => {
        // The buffer is KEPT on failure. The spend was all-or-nothing, so
        // nothing was taken, and clearing it here would make a transient error
        // cost the player their whole plan.
        this.savingPoints = false;
        this.showMessage(errorMessage(err));
        this.render();
      },
    );
  }

  /** Rule 7 (packForRaid) never rejects on capacity — it just silently leaves the item in HIDEOUT. Pre-check so a full bag doesn't look like a dead button. */
  private tryPack(itemId: string): void {
    const { inventory } = gameStateStore.get();
    const item = inventory?.hideout.find((i) => i.id === itemId);
    if (!inventory || !item) return;
    const fits = inventory.carriedWeight + item.weight <= inventory.carryWeightCap
      && inventory.carriedSlotCount + 1 <= inventory.carriedSlotCap;
    if (!fits) {
      this.showMessage(`Won't fit in your carried bag (${item.weight.toFixed(1)}kg — check weight/slot capacity)`);
      return;
    }
    this.clearMessage();
    packItem(itemId);
  }

  /** health/character are updated via their own socket push (see `useItem`'s
   * docstring) — this is purely the receipt text, not a state write. */
  private useReceipt(result: UseItemResult): string {
    return `${result.itemName}: ${consumableEffectText(result.effect)}`;
  }

  private showMessage(text: string): void {
    this.lastActionError = text;
    this.render();
  }

  private clearMessage(): void {
    if (!this.lastActionError) return;
    this.lastActionError = null;
    this.render();
  }

  private render(): void {
    const {
      character, equipment, inventory, inventoryRejection,
    } = gameStateStore.get();

    for (const slot of EQUIPMENT_SLOTS) {
      const cell = this.slotEls.get(slot)!;
      const itemEl = cell.querySelector('.bv-pd-slot-item')!;
      const item: EquippedItemSummary | undefined = equipment?.slots[slot];
      cell.classList.toggle('bv-pd-slot-broken', !!item?.broken);
      if (item) {
        // Icon only: the name, condition, armour and stat lines all live in the
        // hover/tap tooltip (`tipHtmlFor`). The name stays in the DOM as
        // screen-reader text so the cell is not a silent picture.
        const iconHtml = pictureHtml(item.def.iconPath, item.def.name, 'bv-pd-slot-icon', '56px', { kind: item.def.kind, slot: item.def.slot, key: item.def.key });
        itemEl.innerHTML = `${iconHtml}<span class="bv-pd-slot-sr">${escapeHtml(item.def.name)} (${item.durability}/${item.maxDurability}${item.broken ? ' · broken' : ''})</span>`
          + `<button type="button" class="bv-pd-unequip" data-action="unequip" data-slot="${slot}" title="Unequip" aria-label="Unequip ${escapeHtml(item.def.name)}">×</button>`;
      } else {
        itemEl.innerHTML = '<span class="bv-pd-slot-sr">Empty</span>';
      }
    }

    for (const t of TABS) {
      const btn = this.tabEls.get(t.id)!;
      const on = this.tab === t.id;
      btn.classList.toggle('bv-pd-tab-on', on);
      btn.setAttribute('aria-selected', String(on));
      this.paneEls.get(t.id)!.hidden = !on;
    }

    if (character?.state === 'ACTIVE') {
      const points = character.attributePoints;
      this.titleEl.textContent = `Survivor Record — Level ${character.level}`;
      // The unspent-points badge sits on the tab, not just inside the pane:
      // banked points are the one thing in here that rots if ignored, and the
      // Inventory tab is the one that opens by default.
      const skillsTab = this.tabEls.get('skills')!;
      skillsTab.textContent = points > 0 ? `Skills (${points})` : 'Skills';
      skillsTab.classList.toggle('bv-pd-tab-badge', points > 0);

      // Points are STAGED, not spent on the click — Janeks: *"add skill points
      // and then hit 'save' to apply."* `left` is what remains of the bank
      // after the buffer, and it is what the `+` buttons gate on.
      const stagedTotal = this.stagedTotal();
      const left = points - stagedTotal;
      this.attrsEl.innerHTML = `
        <div class="bv-pd-attrs-head">
          <span>Attributes</span>
          <span class="${left > 0 ? 'bv-pd-points-on' : 'bv-pd-points'}">${
        left > 0 ? `${left} point${left === 1 ? '' : 's'} to spend` : 'No points to spend'
      }</span>
        </div>
        <div class="bv-pd-attr-grid">`
        + ATTRIBUTE_ROWS.map((r) => {
          const staged = this.staged[r.code] ?? 0;
          const base = character.attributes[r.key];
          const atMax = base + staged >= character.attrMax;
          // Disabled with a reason rather than hidden: a player at 0 points
          // needs to see that spending exists and comes from levelling. The
          // ceiling comes from the SHEET (`attrMax`, admin-tunable config) so
          // the `+` dies AT the cap instead of the player finding out by
          // having a whole staged set rejected on Save.
          const plusTitle = atMax
            ? `${r.label} is at its maximum of ${character.attrMax}`
            : (left > 0 ? `Stage a point on ${r.label}` : 'Level up to bank a point');
          const plus = `<button type="button" class="bv-pd-alloc" data-action="allocate"`
            + ` data-attr="${r.code}"${left > 0 && !atMax ? '' : ' disabled'}`
            + ` title="${escapeHtml(plusTitle)}">+</button>`;
          // The minus exists only once there is something to take back, so the
          // row is unchanged for a player who is not mid-plan.
          // Its CELL is always emitted even when the button is not, or the `+`
          // would slide left on the rows that have nothing staged and the
          // column of buttons would stop lining up.
          const minus = staged > 0
            ? `<button type="button" class="bv-pd-alloc bv-pd-unalloc" data-action="unstage"`
              + ` data-attr="${r.code}" title="Take the point back off ${escapeHtml(r.label)}">−</button>`
            : '<span class="bv-pd-alloc-gap" aria-hidden="true"></span>';
          // Base and pending are two spans, not one summed number: "3 +2" says
          // what would happen, where "5" would look like it already had.
          const pending = staged > 0 ? `<em class="bv-pd-attr-pending">+${staged}</em>` : '';
          // A sweet or a book: shown beside the number, never summed into it —
          // it is not a spent point and it runs out. Filtered against the
          // clock here as well as on the server, so a sheet that has not been
          // re-fetched since the buff lapsed does not keep showing it.
          const buff = (character.buffs ?? [])
            .find((b) => b.attribute === r.key && Date.parse(b.until) > Date.now());
          const buffTag = buff
            ? `<em class="bv-pd-attr-buff" title="${escapeHtml(`Buffed until ${clockTime(buff.until)}`)}">+${buff.amount}</em>`
            : '';
          return `<div class="bv-pd-attr-row bv-pd-attr-card" title="${escapeHtml(r.note)}">
            <img class="bv-pd-attr-icon" src="/icons/attributes/${r.code.toLowerCase()}.webp"
              alt="" width="48" height="48" decoding="async" draggable="false">
            <div class="bv-pd-attr-main">
              <span class="bv-pd-attr-name">${r.label}<em>${r.code}</em></span>
              <span class="bv-pd-attr-note">${escapeHtml(r.note)}</span>
            </div>
            <span class="bv-pd-attr-val">${base}${buffTag}${pending}</span>
            <div class="bv-pd-attr-ctl">${minus}${plus}</div>
          </div>`;
        }).join('')
        + SIXTH_SKILL_CARD_HTML
        + '</div>'
        + this.stagedFooter(stagedTotal);

      const specId = character.specializationId;
      // Locked reads as a disabled panel with hazard tape across it, and the
      // "Locked" line is printed ON the tape so it follows the tape's angle.
      this.specEl.classList.toggle('bv-pd-spec-locked', !specId);
      this.specEl.innerHTML = specId
        ? `<div class="bv-pd-spec-head">Specialization</div>
           <div class="bv-pd-spec-on">${escapeHtml(SPECIALIZATION_LABELS[specId] ?? specId)}</div>`
        : `<div class="bv-pd-spec-head">Specialization</div>
           <div class="bv-pd-spec-tape"><div class="bv-pd-spec-off">Locked — reach level 5</div></div>`;
    }

    const stats = equipment?.blendedStats;
    if (stats) {
      // Base (attributes alone) / gear / total, so what the kit is adding is
      // legible instead of folded into one number. Before the sheet loads the
      // base falls back to the total and the gear column reads as nothing.
      const base = character?.state === 'ACTIVE' ? character.stats : stats;
      this.statsEl.innerHTML = '<div class="bv-pd-stats-head">Stats</div>'
        + '<div class="bv-pd-stat-row bv-pd-stat-cols"><span></span><span>Base</span><span>Gear</span><span>Total</span></div>'
        + STAT_ROWS.map((r) => {
          const b = base[r.key];
          const t = stats[r.key];
          const g = Math.round((t - b) * 10 ** r.digits) / 10 ** r.digits;
          const gear = g === 0 ? '—' : `${g > 0 ? '+' : ''}${g.toFixed(r.digits)}`;
          const cls = g > 0 ? ' bv-pd-gear-up' : (g < 0 ? ' bv-pd-gear-down' : '');
          return `<div class="bv-pd-stat-row"><span>${r.label}</span><span>${b.toFixed(r.digits)}</span>`
            + `<span class="bv-pd-gear${cls}">${gear}</span><span>${t.toFixed(r.digits)}</span></div>`;
        }).join('');
    }

    if (inventory) {
      this.setCapacity(
        '[data-cap-weight]', '[data-weight-text]', '[data-weight-fill]',
        inventory.carriedWeight, inventory.carryWeightCap,
        `${inventory.carriedWeight.toFixed(1)} / ${inventory.carryWeightCap.toFixed(1)} kg`,
        worstLevel(
          capacityLevel(inventory.carriedWeight, inventory.carryWeightCap),
          capacityLevel(inventory.carriedSlotCount, inventory.carriedSlotCap),
        ),
      );
      this.setText('[data-slots-text]', `${inventory.carriedSlotCount} / ${inventory.carriedSlotCap} slots`);
      this.setCapacity(
        '[data-cap-hideout]', '[data-hideout-text]', '[data-hideout-fill]',
        inventory.hideoutSlotCount, inventory.hideoutSlotCap,
        `${inventory.hideoutSlotCount} / ${inventory.hideoutSlotCap}`,
      );
      // Rebuilt every render, before the grids that read it: the pool map is
      // what `transfer` and the tooltip dispatch on, so it must never describe
      // a previous sheet.
      this.poolById.clear();
      this.itemById.clear();
      for (const item of inventory.carried) { this.poolById.set(item.id, 'carried'); this.itemById.set(item.id, item); }
      for (const item of inventory.hideout) { this.poolById.set(item.id, 'hideout'); this.itemById.set(item.id, item); }

      this.carriedEl.innerHTML = this.renderItemGrid(inventory.carried, inventory.carriedSlotCap);
      this.hideoutEl.innerHTML = this.renderItemGrid(inventory.hideout, inventory.hideoutSlotCap);
      // Contents, not a third copy of the meter's numbers. Before this the
      // stash read "0/40 slots" in the head, "—" beside the bar and "0 / 40"
      // inside it — three readouts of one number, one of which was never
      // filled in. Seen in a screenshot, invisible to every test.
      const worn = equipment ? EQUIPMENT_SLOTS.filter((sl) => equipment.slots[sl]).length : 0;
      this.carriedSubEl.textContent = `${worn} worn · ${inventory.carried.length} carried`;
      this.hideoutSubEl.textContent = `${inventory.hideout.length} stored`;
      // A card left up over a slot that just moved pools would describe the
      // wrong item — the grid under the pointer is rebuilt wholesale here.
      this.tooltip.hide();
      this.pinned.hide();
    }

    const message = this.lastActionError ?? (inventoryRejection ? (inventoryRejection.message ?? inventoryRejection.reason) : null);
    this.msgEl.hidden = !message;
    this.msgEl.textContent = message ?? '';
  }

  /**
   * One capacity meter: label text, bar width, and the pressure class driving
   * its colour.
   *
   * Queries `this.el`, not the capacity element: the three meters no longer
   * live together — weight and carried slots sit in the ON YOU block while the
   * hideout's is inside the HIDEOUT block, which is the split the two-block
   * layout exists to draw.
   */
  private setCapacity(
    boxSel: string, textSel: string, fillSel: string,
    used: number, cap: number, text: string,
    /** Override the pressure class — the carry meter is coloured by the WORSE of its two caps. */
    level = capacityLevel(used, cap),
  ): void {
    const box = this.el.querySelector(boxSel) as HTMLElement | null;
    if (box) {
      box.classList.remove('bv-cap-ok', 'bv-cap-warn', 'bv-cap-critical');
      box.classList.add(`bv-cap-${level}`);
    }
    const textEl = this.el.querySelector(textSel);
    if (textEl) textEl.textContent = text;
    const fill = this.el.querySelector(fillSel) as HTMLElement | null;
    if (fill) fill.style.width = `${fillPercent(used, cap)}%`;
  }

  /** One text node, by selector. */
  private setText(sel: string, text: string): void {
    const node = this.el.querySelector(sel);
    if (node) node.textContent = text;
  }

  /**
   * Fixed-size grid for a variable-length item array — `gridSlotCount`
   * (ui/item-slot.ts) has the growth rule. Every visible slot renders
   * SOMETHING, a filled cell or an explicit empty one, so the pool reads as a
   * bag even when it holds nothing.
   */
  private renderItemGrid(items: InventoryItemSummary[], cap: number): string {
    const totalSlots = gridSlotCount(items.length, PD_COLUMNS, PD_MIN_ROWS);
    const cells: string[] = [];
    for (let i = 0; i < totalSlots; i += 1) {
      const item = items[i];
      // The grid is 6 x 5 whatever the limit is, so cells past the real cap are
      // drawn LOCKED: a plain empty cell there would promise room the server
      // will refuse.
      if (item) cells.push(this.itemSlot(item));
      else if (i >= cap) cells.push('<div class="bv-slot bv-slot-empty bv-slot-locked" title="Beyond your slot limit"></div>');
      else cells.push(emptySlotHtml());
    }
    return cells.join('');
  }

  /**
   * One filled slot: the art, a stack count, and the actions revealed on hover.
   *
   * Which actions is a function of the pool, and the asymmetry is the game's,
   * not a UI choice:
   *  - **Equip** reads from the hideout ONLY (inventory.md rule 10, enforced
   *    server-side). A carried equipable still shows the button, DISABLED with
   *    the reason — hiding it would make the rule undiscoverable, and leaving
   *    it live would make it a button that always 409s.
   *  - **Discard** is hideout-only, unchanged: the carried bag is emptied by
   *    storing or by dying, and a one-click destroy on the bag you are about
   *    to take into a raid is a different, riskier control.
   */
  /**
   * The actions a click on a slot offers — shown in the pinned tooltip, not on
   * the slot. Equip works from either pool (inventory.md rule 10, lifted
   * 2026-10-14); Discard stays hideout-only, as before.
   */
  private actionsFor(item: InventoryItemSummary, pool: Pool): SlotAction[] {
    const actions: SlotAction[] = [];
    if (item.usable) actions.push({ action: 'use', label: 'Use', variant: 'go' });
    if (item.slot) actions.push({ action: 'equip', label: 'Equip', variant: 'go' });
    actions.push({
      action: 'transfer',
      label: pool === 'carried' ? 'To hideout' : 'To bag',
    });
    if (pool === 'hideout') actions.push({ action: 'discard', label: 'Discard', variant: 'danger' });
    return actions;
  }

  private actionButtons(actions: SlotAction[], attrs: string): string {
    const variant: Record<string, string> = { go: ' bv-slot-btn-go', danger: ' bv-slot-btn-danger', buy: ' bv-slot-btn-buy' };
    return actions.map((a) => `<button type="button" class="bv-slot-btn${a.variant ? variant[a.variant] : ''}"`
      + ` data-action="${a.action}" ${attrs}${a.disabled ? ' disabled' : ''}>${escapeHtml(a.label)}</button>`).join('');
  }

  /** Click on a slot: pin its card, with the action buttons, beside the slot. */
  private pin(host: HTMLElement): void {
    const tipId = host.dataset.tip!;
    const base = this.tipHtmlFor(tipId);
    if (!base) { this.pinned.hide(); return; }
    let actions: string;
    if (tipId.startsWith('eq:')) {
      const slot = tipId.slice(3);
      actions = this.actionButtons([{ action: 'unequip', label: 'Unequip' }], `data-slot="${slot}"`);
    } else {
      const item = this.itemById.get(tipId);
      const pool = this.poolById.get(tipId);
      if (!item || !pool) return;
      actions = this.actionButtons(this.actionsFor(item, pool), `data-item-id="${escapeHtml(tipId)}"`);
    }
    this.tooltip.hide();
    const r = host.getBoundingClientRect();
    this.pinned.show(`${base}<div class="bv-tip-actions">${actions}</div>`, r.right, r.top);
  }

  /**
   * Double-click an item: equip it, or — when it does not wear anywhere — send it
   * to the other pool. The first click of the pair pinned a card; this dismisses it.
   */
  private readonly onDoubleClick = (e: MouseEvent): void => {
    const el = (e.target as HTMLElement).closest('.bv-slot-filled[data-item-id]') as HTMLElement | null;
    const id = el?.dataset.itemId;
    const item = id ? this.itemById.get(id) : undefined;
    if (!id || !item) return;
    e.preventDefault();
    this.pinned.hide();
    this.tooltip.hide();
    if (item.slot) this.runAction(equipItem(id, item.slot));
    else if (this.poolById.get(id) === 'hideout') this.tryPack(id);
    else { this.clearMessage(); storeItem(id); }
  };

  private readonly onPinnedClick = (e: MouseEvent): void => {
    if ((e.target as HTMLElement).closest('button[data-action]')) this.onClick(e);
  };

  private readonly onDocPointerDown = (e: PointerEvent): void => {
    if (!this.pinned.isVisible) return;
    if (this.pinned.el.contains(e.target as Node)) return;
    this.pinned.hide();
  };

  // ─── drag an item onto its worn slot ──────────────────────────────────────

  private readonly onSlotPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0 || this.drag) return;
    const el = (e.target as HTMLElement).closest('.bv-slot-filled[data-item-id]') as HTMLElement | null;
    const item = el ? this.itemById.get(el.dataset.itemId!) : undefined;
    if (!el || !item) return;
    this.drag = {
      itemId: item.id, slot: item.slot ?? null, from: this.poolById.get(item.id) ?? 'carried', x: e.clientX, y: e.clientY, started: false,
      pointerType: e.pointerType, ghost: null, timer: null, srcEl: el,
    };
    // Touch has to disambiguate from scrolling the grid: a short hold starts the drag.
    if (e.pointerType === 'touch') this.drag.timer = window.setTimeout(() => this.startDrag(e.clientX, e.clientY), 250);
    document.addEventListener('pointermove', this.onDragMove);
    document.addEventListener('pointerup', this.onDragEnd);
    document.addEventListener('pointercancel', this.onDragEnd);
  };

  private startDrag(x: number, y: number): void {
    const d = this.drag;
    if (!d || d.started) return;
    d.started = true;
    this.tooltip.hide();
    this.pinned.hide();
    const img = d.srcEl.querySelector('img');
    const ghost = document.createElement('div');
    ghost.className = 'bv-drag-ghost';
    if (img) { const c = img.cloneNode() as HTMLImageElement; c.draggable = false; ghost.appendChild(c); }
    document.body.appendChild(ghost);
    d.ghost = ghost;
    d.srcEl.classList.add('bv-slot-dragging');
    this.el.classList.add('bv-pd-dragging');
    if (d.slot) this.slotEls.get(d.slot)?.classList.add('bv-pd-slot-target');
    this.poolEl(this.otherPool(d.from)).classList.add('bv-pd-pool-target');
    document.addEventListener('touchmove', this.blockScroll, { passive: false });
    this.moveGhost(x, y);
  }

  private moveGhost(x: number, y: number): void {
    const d = this.drag;
    if (!d?.ghost) return;
    d.ghost.style.transform = `translate(${x}px, ${y}px) rotate(-6deg) scale(1.08)`;
    const over = this.dropTargetAt(x, y);
    if (d.slot) this.slotEls.get(d.slot)?.classList.toggle('bv-pd-slot-over', over === 'slot');
    this.poolEl(this.otherPool(d.from)).classList.toggle('bv-pd-pool-over', over === 'pool');
  }

  private otherPool(p: Pool): Pool { return p === 'carried' ? 'hideout' : 'carried'; }
  private poolEl(p: Pool): HTMLElement { return p === 'carried' ? this.carriedEl : this.hideoutEl; }

  /**
   * What the pointer is over that this item can be dropped on: its own worn
   * slot (equip), or the OTHER pool's grid (transfer). Anything else is no drop.
   */
  private dropTargetAt(x: number, y: number): 'slot' | 'pool' | null {
    const d = this.drag;
    if (!d) return null;
    const at = document.elementFromPoint(x, y);
    if (!at) return null;
    if (d.slot) {
      const hit = at.closest('.bv-pd-slot');
      if (hit && hit === this.slotEls.get(d.slot)) return 'slot';
    }
    if (at.closest('.bv-pd-items') === this.poolEl(this.otherPool(d.from))) return 'pool';
    return null;
  }

  private readonly blockScroll = (e: TouchEvent): void => { e.preventDefault(); };

  private readonly onDragMove = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d) return;
    if (!d.started) {
      const moved = Math.hypot(e.clientX - d.x, e.clientY - d.y);
      if (d.pointerType === 'touch') { if (moved > 8) this.endDrag(); return; }
      if (moved < 6) return;
      this.startDrag(e.clientX, e.clientY);
      return;
    }
    this.moveGhost(e.clientX, e.clientY);
  };

  private readonly onDragEnd = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d) return;
    if (d.started && e.type === 'pointerup') {
      const hit = this.dropTargetAt(e.clientX, e.clientY);
      if (hit === 'slot' && d.slot) this.runAction(equipItem(d.itemId, d.slot));
      else if (hit === 'pool') {
        if (d.from === 'hideout') this.tryPack(d.itemId);
        else { this.clearMessage(); storeItem(d.itemId); }
      }
    }
    // The click that follows a drag's pointerup must not pin a card.
    if (d.started) {
      this.suppressClick = true;
      window.setTimeout(() => { this.suppressClick = false; }, 60);
    }
    this.endDrag();
  };

  private endDrag(): void {
    const d = this.drag;
    if (!d) return;
    if (d.timer !== null) window.clearTimeout(d.timer);
    d.ghost?.remove();
    d.srcEl.classList.remove('bv-slot-dragging');
    this.el.classList.remove('bv-pd-dragging');
    if (d.slot) this.slotEls.get(d.slot)?.classList.remove('bv-pd-slot-target', 'bv-pd-slot-over');
    this.carriedEl.classList.remove('bv-pd-pool-target', 'bv-pd-pool-over');
    this.hideoutEl.classList.remove('bv-pd-pool-target', 'bv-pd-pool-over');
    this.drag = null;
    document.removeEventListener('pointermove', this.onDragMove);
    document.removeEventListener('pointerup', this.onDragEnd);
    document.removeEventListener('pointercancel', this.onDragEnd);
    document.removeEventListener('touchmove', this.blockScroll);
  }

  private itemSlot(item: InventoryItemSummary): string {
    const iconHtml = pictureHtml(item.iconPath, item.name, '', '78px', { kind: item.kind, slot: item.slot, key: item.itemId });
    return slotHtml({
      itemId: item.id,
      name: item.name,
      iconPath: item.iconPath,
      quantity: item.quantity,
      worn: false,
      tipId: item.id,
    }, iconHtml);
  }

  dispose(): void {
    this.unsub();
    disposeWardrobePreview();
    this.endDrag();
    document.removeEventListener('pointerdown', this.onDocPointerDown, true);
    this.tooltip.dispose();
    this.pinned.dispose();
    this.el.remove();
  }
}

/** `14:32` in the player's own clock — for the buff tag's tooltip. */
function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
